/**
 * LanguagePicker — the canonical language snippet across ALL modules.
 *
 * Source of truth was `HustleFinderPage.jsx` inline picker; extracted here
 * so every module (hustlefind, hustleplaza, hustleeats, hustledirectory,
 * hustleshop, restaurant microsites, …) renders IDENTICALLY.
 *
 * Design platform: snippet-first. Never fork this. If a caller needs a
 * variant, pass a prop — don't duplicate.
 *
 * Uses react-i18next for translation state (same as the rest of the app),
 * SUPPORTED_LANGUAGES from `@/i18n` for the master language catalogue,
 * localStorage `i18nextLng` for persistence.
 */
import { useEffect, useRef, useState } from "react";
import Tip from "@/components/Tip";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";

/* ── googtrans cookie helpers ───────────────────────────────────────
 * Google Translate reads the `googtrans` cookie to force a translation
 * on every page load. If we only change the i18n state, the picker will
 * show "English" while a stale `googtrans=/auto/vi` cookie continues to
 * force Vietnamese content. So we treat this cookie as the second half
 * of the language state and keep both in sync.
 */
function _hosts() {
  const h = typeof window !== "undefined" ? window.location.hostname : "";
  const parts = h.split(".");
  // For hustlefind.au we set cookies on hustlefind.au + .hustlefind.au
  const bases = new Set();
  if (h) bases.add(h);
  if (parts.length >= 2) bases.add(parts.slice(-2).join("."));
  return [...bases];
}
function _readGoogtrans() {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)googtrans=\/[^/]+\/([^;]+)/);
  return m ? m[1] : null;
}
function _writeGoogtrans(code) {
  if (typeof document === "undefined") return;
  // Clear any prior cookie on every possible host/path scope.
  const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
  for (const host of _hosts()) {
    document.cookie = `googtrans=;path=/;expires=${expired}`;
    document.cookie = `googtrans=;path=/;domain=${host};expires=${expired}`;
    document.cookie = `googtrans=;path=/;domain=.${host};expires=${expired}`;
  }
  if (code && code !== "en") {
    const value = `/auto/${code}`;
    for (const host of _hosts()) {
      document.cookie = `googtrans=${value};path=/`;
      document.cookie = `googtrans=${value};path=/;domain=${host}`;
      document.cookie = `googtrans=${value};path=/;domain=.${host}`;
    }
  }
}

export default function LanguagePicker({
  align = "right",             // "right" | "left"
  size = "sm",                 // "sm" | "md"
  className = "",
  testIdPrefix = "language",   // e.g. "finder-language" preserves old testids
}) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // On mount: reconcile the googtrans cookie with i18n state. If
  // googtrans forces a language different from i18nextLng, googtrans
  // wins (it's what's actually being displayed) and we sync the picker.
  useEffect(() => {
    const forced = _readGoogtrans();
    if (forced && forced !== i18n.language) {
      i18n.changeLanguage(forced);
      try { window.localStorage.setItem("i18nextLng", forced); } catch (_e) { /* ignore */ }
    }
    // Intentionally only on mount; downstream changes go through choose().
  }, []);

  const active = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)
                  || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = (code) => {
    i18n.changeLanguage(code);
    try { window.localStorage.setItem("i18nextLng", code); } catch (_e) { /* ignore */ }
    // Sync Google Translate's cookie too — otherwise it will keep
    // forcing the previous language on top of our i18n change.
    _writeGoogtrans(code);
    setOpen(false);
    // Full reload so Google Translate re-scans the page against the new
    // cookie. Without this, the current tab keeps the old translation
    // and drifts out of sync with what the picker shows.
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  const btnCls = size === "md"
    ? "text-sm text-slate-700 hover:text-emerald-700"
    : "text-xs text-slate-600 hover:text-emerald-700";
  const menuPos = align === "left" ? "left-0" : "right-0";

  return (
    <div ref={ref} className={`relative ${className}`} data-testid={`${testIdPrefix}-picker`}>
      <Tip label="Click here to select your language">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={`${testIdPrefix}-button`}
        className={`flex items-center gap-1 ${btnCls}`}
        aria-label={`Change language, currently ${active.native}`}
        aria-expanded={open}
      >
        <span className="text-base leading-none">{active.flag || "🌐"}</span>
        <span className="font-medium">{active.native}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      </Tip>
      {open && (
        <div
          data-testid={`${testIdPrefix}-menu`}
          role="listbox"
          className={`absolute ${menuPos} mt-2 w-60 max-h-80 overflow-y-auto
                     bg-white border border-slate-200 rounded-xl shadow-lg z-20`}
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => choose(l.code)}
              data-testid={`${testIdPrefix}-option-${l.code}`}
              role="option"
              aria-selected={l.code === active.code}
              className={`w-full flex items-center gap-2 text-left px-3 py-1.5 text-sm hover:bg-emerald-50
                          ${l.code === active.code ? "bg-emerald-50 font-semibold" : ""}`}
            >
              <span className="text-base leading-none w-6">{l.flag || "🌐"}</span>
              <span className="text-slate-800 flex-1">{l.native}</span>
              <span className="text-slate-400 text-xs">{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
